import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import { mapActions, mapState } from "pinia";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import { useWindowStore } from "@vcl";

const ElementResizingMixinStaging = {
  data() {
    return {
      scaleFactor: 1,
      resizing: null,
      resizingStart: null,
      debounce: null,
      relatedElement: null,
    };
  },
  computed: {
    ...mapState(useDynamicComponentBuilderStore, [
      "activeState",
    ]),
    canResizeHorizontally() {
      const [state, breakpoint] = this.activeState.split(":");
      const layoutWidth = this.dynamicComponentInstance.getRawOptionValue(
        this.identifier,
        "layout-width",
        breakpoint,
        state,
      )?.r;
      return this.editMode && typeof layoutWidth === "number";
    },
    canResizeVertically() {
      const [state, breakpoint] = this.activeState.split(":");
      const layoutHeight = this.dynamicComponentInstance.getRawOptionValue(
        this.identifier,
        "layout-height",
        breakpoint,
        state,
      )?.r;
      return this.editMode && typeof layoutHeight === "number";
    },
  },
  mounted() {
    document.addEventListener("mousemove", this.handleResize);
    document.addEventListener("mouseup", this.handleResizeStop);
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.handleResize);
    document.removeEventListener("mouseup", this.handleResizeStop);
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, [
      "findElementInLayout",
    ]),
    handleMouseDownResize(event, direction) {
      this.resizing = direction;
      this.relatedElement = this.dynamicComponentInstance.getElementById(this.identifier);
      sessionStorage.setItem("resizing", this.identifier || "true");

      const element = this.$el || event.target.closest(`[data-identifier="${this.identifier}"]`);
      this.scaleFactor = useWindowStore().calculateScaleFactor(element);

      this.originalWidth = this.getOptionValue({ optionKey: "layout-width" });
      this.originalHeight = this.getOptionValue({ optionKey: "layout-height" });
      this.startMouseX = event.clientX;
      this.startMouseY = event.clientY;

      if (["top", "bottom"].includes(direction)) {
        this.resizingStart = event.clientY;
      } else {
        this.resizingStart = event.clientX;
      }
    },

    handleResize(event) {
      if (sessionStorage.getItem("resizing") === this.identifier) {
        if (["top", "bottom"].includes(this.resizing)) {
          this.handleResizeVertically(event);
        } else {
          this.handleResizeHorizontally(event);
        }
      }
    },

    handleResizeHorizontally(event) {
      const rawMouseDiff = event.clientX - this.startMouseX;
      const scaledMouseDiff = rawMouseDiff / this.scaleFactor;

      let newWidth;
      if (this.resizing === "left") {
        newWidth = this.originalWidth - scaledMouseDiff;
      } else {
        newWidth = this.originalWidth + scaledMouseDiff;
      }

      if (newWidth > 0) {
        this.updateOptionConfigValue(
          LayoutWidth,
          Math.round(newWidth),
          `Element width value updated for ${this.dynamicComponentName}`,
        );
      }
    },

    handleResizeVertically(event) {
      const rawMouseDiff = event.clientY - this.startMouseY;
      const scaledMouseDiff = rawMouseDiff / this.scaleFactor;

      let newHeight;
      if (this.resizing === "top") {
        newHeight = this.originalHeight - scaledMouseDiff;
      } else {
        newHeight = this.originalHeight + scaledMouseDiff;
      }

      if (newHeight > 0) {
        this.updateOptionConfigValue(
          LayoutHeight,
          Math.round(newHeight),
          `Element height value updated for ${this.dynamicComponentName}`,
        );
      }
    },
    updateOptionConfigValue(optionClass, value) {
      const [state, breakpoint] = this.activeState.split(":");
      this.dynamicComponentInstance.modifyElementOption(
        this.identifier,
        optionClass.optionKey,
        value,
        breakpoint,
        state,
      );
    },
    handleResizeStop() {
      if (this.resizing) {
        sessionStorage.removeItem("resizing");
        this.mouseOver = false;
        this.resizing = null;
        this.scaleFactor = 1;
        this.startMouseX = null;
        this.startMouseY = null;
        this.relatedElement = null;
      }
    },
  },
};

export { ElementResizingMixinStaging };
